import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, CardTitle, Button, Row, Col, Badge, Table } from 'react-bootstrap';
import packRepository from '../../services/repositories/pack.repository';
import '../../styles/products.scss';

const PackShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pack, setPack] = useState(null);
  const [packProducts, setPackProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPackDetails();
  }, [id]);

  const fetchPackDetails = async () => {
    try {
      setLoading(true);
      const response = await packRepository.getPackWithProducts(id);
      setPack(response.pack);
      setPackProducts(response.pack_products || []);
    } catch (err) {
      setError('Erreur lors du chargement des détails du pack');
      console.error('Error fetching pack details:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? (
      <Badge bg="success">Actif</Badge>
    ) : (
      <Badge bg="danger">Inactif</Badge>
    );
  };

  const calculateTotalValue = () => {
    return packProducts.reduce((total, item) => {
      return total + (item.product?.price || 0) * item.quantity;
    }, 0);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
        <p className="mt-3">Chargement des détails du pack...</p>
      </div>
    );
  }

  if (error || !pack) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-danger" role="alert">
          {error || 'Pack non trouvé'}
        </div>
        <Link to="/admin/packs/pack-list">
          <Button variant="primary">Retour à la liste</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="pack-show-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">{pack.name}</h4>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/admin/packs/pack-list">Packs Produit</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {pack.name}
              </li>
            </ol>
          </nav>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            onClick={() => navigate(`/admin/packs/pack-edit/${pack.id}`)}
          >
            <i className="mdi mdi-pencil"></i> Modifier
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/admin/packs/pack-list')}
          >
            <i className="mdi mdi-arrow-left"></i> Retour
          </Button>
        </div>
      </div>

      <Row>
        <Col lg={8}>
          <Card className="mb-4">
            <CardHeader>
              <CardTitle as="h5" className="mb-0">Produits du Pack</CardTitle>
            </CardHeader>
            <CardBody>
              {packProducts.length === 0 ? (
                <div className="text-center py-4">
                  <i className="mdi mdi-package-variant-closed display-4 text-muted"></i>
                  <p className="mt-3 text-muted">Aucun produit dans ce pack</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Image</th>
                        <th>Produit</th>
                        <th>Prix Unitaire</th>
                        <th>Quantité</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {packProducts.map((item, index) => (
                        <tr key={index}>
                          <td>
                            {item.product?.image ? (
                              <img
                                src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}/storage/${item.product.image}`}
                                alt={item.product.name}
                                className="rounded"
                                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                              />
                            ) : (
                              <div
                                className="bg-light rounded d-flex align-items-center justify-content-center"
                                style={{ width: '50px', height: '50px' }}
                              >
                                <i className="mdi mdi-image text-muted"></i>
                              </div>
                            )}
                          </td>
                          <td>
                            <div>
                              <strong>{item.product?.name || 'Produit inconnu'}</strong>
                              {item.product?.description && (
                                <div className="text-muted small mt-1">
                                  {item.product.description.length > 50
                                    ? `${item.product.description.substring(0, 50)}...`
                                    : item.product.description}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>{item.product?.price || 0} DH</td>
                          <td>
                            <Badge bg="primary" className="fs-6 px-2 py-1">
                              {item.quantity}
                            </Badge>
                          </td>
                          <td className="fw-bold">
                            {((item.product?.price || 0) * item.quantity).toFixed(2)} DH
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan="4" className="text-end fw-bold">Total des produits:</td>
                        <td className="fw-bold text-primary">
                          {calculateTotalValue().toFixed(2)} DH
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="mb-4">
            <CardHeader>
              <CardTitle as="h5" className="mb-0">Informations du Pack</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="mb-3">
                <label className="form-label fw-bold">Nom:</label>
                <p className="mb-0">{pack.name}</p>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Prix du Pack:</label>
                <p className="mb-0 fs-5 text-primary fw-bold">{pack.price} DH</p>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Statut:</label>
                <div className="mt-1">{getStatusBadge(pack.status)}</div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Quantité en Stock:</label>
                <p className="mb-0">{pack.quantity}</p>
              </div>

              {pack.description && (
                <div className="mb-3">
                  <label className="form-label fw-bold">Description:</label>
                  <p className="mb-0">{pack.description}</p>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label fw-bold">Nombre de Produits:</label>
                <p className="mb-0">{packProducts.length} produit(s)</p>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Date de Création:</label>
                <p className="mb-0">{new Date(pack.created_at).toLocaleDateString('fr-FR')}</p>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle as="h6" className="mb-0">Actions</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  onClick={() => navigate(`/admin/packs/pack-edit/${pack.id}`)}
                  className="w-100"
                >
                  <i className="mdi mdi-pencil me-2"></i>Modifier le Pack
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={() => navigate('/admin/packs/pack-list')}
                  className="w-100"
                >
                  <i className="mdi mdi-arrow-left me-2"></i>Retour à la Liste
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PackShow;